import React, { useState } from 'react';
import { useQuery } from '@/apollo';
import { gql } from '@/apollo';
import { motion } from 'framer-motion';
import { RichTextDisplay } from '../ui/RichTextDisplay';
import { CollapsibleRichText } from '../ui/CollapsibleRichText';

// Dark scrollbar CSS for Provider Pool dropdowns
const DARK_SCROLLBAR_STYLES = `
  .dark-scrollbar::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  
  .dark-scrollbar::-webkit-scrollbar-track {
    background: var(--color-bg-tertiary, #1a1a1a);
    border-radius: 4px;
  }
  
  .dark-scrollbar::-webkit-scrollbar-thumb {
    background: var(--color-border, #374151);
    border-radius: 4px;
    border: 1px solid var(--color-bg-tertiary, #1a1a1a);
  }
  
  .dark-scrollbar::-webkit-scrollbar-thumb:hover {
    background: var(--color-text-muted, #6b7280);
  }
  
  .dark-scrollbar::-webkit-scrollbar-corner {
    background: var(--color-bg-tertiary, #1a1a1a);
  }
  
  /* Firefox dark scrollbar */
  .dark-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: var(--color-border, #374151) var(--color-bg-tertiary, #1a1a1a);
  }
`;
import { 
  User, 
  Users,
  Phone, 
  MapPin, 
  AlertTriangle, 
  Search,
  Stethoscope,
  Shield,
  FileText,
  CheckCircle,
  XCircle,
  Building,
  ChevronDown,
  Clock
} from 'lucide-react';

// GraphQL Query to get provider profiles from Moderator Dashboard
const GET_MODERATOR_PROVIDERS = gql`
  query GetModeratorProviders {
    moderatorData {
      providers {
        id
        firstName
        lastName
        title
        customerId
        customer {
          id
          name
        }
        phoneNumbers {
          id
          name
          phone
          location {
            id
            name
            city
            state {
              id
              code
              name
            }
            zipCode
            physicalAddress
          }
        }
        maLvnName
        maLvnPhone
        triageNurseName
        triageNursePhone
        surgerySchedulerName
        surgerySchedulerPhone
        practiceAdminName
        practiceAdminPhone
        conditions {
          id
          condition {
            id
            name
            category
          }
        }
        locations {
          id
          location {
            id
            name
            city
            state {
              id
              code
              name
            }
            zipCode
            physicalAddress
          }
          address
          suite
          phone
        }
        insurances {
          id
          insurance {
            id
            name
          }
        }
        notes {
          id
          content
          category
          order
          createdAt
        }
        apps {
          id
          firstName
          lastName
          title
        }
        supervisingProviders {
          id
          firstName
          lastName
          title
        }
        schedulingInstructions
        insuranceNotes
        isActive
        acceptsNewPatients
        requiresReferral
        createdAt
        updatedAt
      }
    }
  }
`;

const GET_CUSTOMERS = gql`
  query GetCustomers {
    customers {
      id
      name
      description
      isActive
    }
  }
`;





interface Customer {
  id: string;
  name: string;
  description?: string;
}

interface ProviderProfile {
  id: string;
  firstName: string;
  lastName: string;
  title?: string;
  customerId?: string;
  customer?: Customer;
  phoneNumbers: Array<{
    id: string;
    name: string;
    phone: string;
    location?: {
      id: string;
      name: string;
      city?: string;
      physicalAddress?: string;
      state?: {
        id: string;
        code: string;
        name: string;
      };
      zipCode?: string;
    };
  }>;
  maLvnName?: string;
  maLvnPhone?: string;
  triageNurseName?: string;
  triageNursePhone?: string;
  surgerySchedulerName?: string;
  surgerySchedulerPhone?: string;
  practiceAdminName?: string;
  practiceAdminPhone?: string;
  conditions: Array<{
    id: string;
    condition: {
      id: string;
      name: string;
      category?: string;
    };
  }>;
  apps?: Array<{
    id: string;
    firstName: string;
    lastName: string;
    title?: string;
  }>;
  supervisingProviders?: Array<{
    id: string;
    firstName: string;
    lastName: string;
    title?: string;
  }>;
  schedulingInstructions?: string;
  insuranceNotes?: string;
  locations: Array<{
    id: string;
    location: {
      id: string;
      name: string;
      physicalAddress?: string;
      state?: {
        id: string;
        code: string;
        name: string;
      };
    };
    address?: string;
    suite?: string;
    phone?: string;
  }>;
  insurances: Array<{
    id: string;
    insurance: {
      id: string;
      name: string;
    };
  }>;
  notes: Array<{
    id: string;
    content: string;
    category?: string;
    order: number;
    createdAt: string;
  }>;
  isActive: boolean;
  acceptsNewPatients?: boolean;
  requiresReferral?: boolean;
  createdAt: string;
  updatedAt: string;
}

export const ProviderPool: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProvider, setSelectedProvider] = useState<ProviderProfile | null>(null);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);

  // Fetch customers
  const { data: customersData } = useQuery(GET_CUSTOMERS, { fetchPolicy: 'network-only' });
  const customers: Customer[] = customersData?.customers?.filter((c: any) => c.isActive) || [];

  // Fetch provider profiles data from Moderator Dashboard
  const { data: moderatorData, loading, error } = useQuery(GET_MODERATOR_PROVIDERS, {
    fetchPolicy: 'network-only', // Always fetch from database
    notifyOnNetworkStatusChange: true
  });

  // Get all providers
  const allProviders: ProviderProfile[] = moderatorData?.moderatorData?.providers || [];
  
  // Filter providers by selected customer
  const providers: ProviderProfile[] = selectedCustomer
    ? allProviders.filter(provider => provider.customerId === selectedCustomer.id)
    : [];

  // Filter suggestions by search term (firstName, lastName, or full name contains term, case-insensitive)
  const searchSuggestions = isSearchFocused && selectedCustomer
    ? providers.filter(provider => {
        if (!searchTerm.trim()) return true;
        const term = searchTerm.trim().toLowerCase();
        const fullName = `${provider.title || 'Dr.'} ${provider.firstName} ${provider.lastName}`.toLowerCase();
        const first = (provider.firstName || '').toLowerCase();
        const last = (provider.lastName || '').toLowerCase();
        return fullName.includes(term) || first.includes(term) || last.includes(term);
      })
    : [];

  // Find the selected provider (including inactive providers!)
  const selectedProviderData = selectedProvider || 
    (searchTerm && providers.find(provider => 
      `${provider.title || 'Dr.'} ${provider.firstName} ${provider.lastName}`.toLowerCase() === searchTerm.toLowerCase()
    ));

  const formatPhone = (phone?: string, extension?: string) => {
    if (!phone) return null;
    
    // Remove all non-numeric characters
    const cleaned = phone.replace(/\D/g, '');
    
    // Format as (XXX) XXX-XXXX
    let formatted = phone;
    if (cleaned.length === 10) {
      formatted = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    
    return extension ? `${formatted} ext. ${extension}` : formatted;
  };



  const getStatusColor = (isActive: boolean) => {
    return isActive ? 'var(--color-success)' : 'var(--color-error)';
  };

  const getStatusIcon = (isActive: boolean) => {
    return isActive ? <CheckCircle className="w-5 h-5" /> : <XCircle className="w-5 h-5" />;
  };



  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: 'var(--color-accent)' }}></div>
        <p className="ml-3 text-lg" style={{ color: 'var(--color-text-secondary)' }}>Loading providers...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <AlertTriangle className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--color-error)' }} />
        <p className="text-lg" style={{ color: 'var(--color-error)' }}>Error loading providers</p>
        <p className="text-sm mt-2" style={{ color: 'var(--color-text-muted)' }}>{error.message}</p>
      </div>
    );
  }

  return (
    <>
      <style>{DARK_SCROLLBAR_STYLES}</style>
      <div className="space-y-6">
      {/* Header removed for streamlined UX */}

      {/* Customer Selection */}
      <div 
        className="rounded-xl shadow-sm border p-6"
        style={{
          backgroundColor: 'var(--color-bg-primary)',
          borderColor: 'var(--color-border)',
          boxShadow: 'var(--shadow-sm)'
        }}
      >
        <div className="relative">
          <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
            <Building className="w-4 h-4 inline mr-2" />
            Choose Customer Organization
          </label>
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowCustomerDropdown(!showCustomerDropdown)}
              className="w-full px-4 py-3 border rounded-lg text-left flex items-center justify-between transition-colors"
              style={{
                backgroundColor: 'var(--color-bg-secondary)',
                borderColor: 'var(--color-border)',
                color: selectedCustomer ? 'var(--color-text-primary)' : 'var(--color-text-muted)'
              }}
            >
              <span>{selectedCustomer ? selectedCustomer.name : 'Select customer...'}</span>
              <ChevronDown className="w-4 h-4" />
            </button>

            {showCustomerDropdown && (
              <div 
                className="absolute top-full left-0 right-0 mt-1 border rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto dark-scrollbar"
                style={{
                  backgroundColor: 'var(--color-bg-primary)',
                  borderColor: 'var(--color-border)',
                  boxShadow: 'var(--shadow-lg)'
                }}
              >
                {customers.map((customer) => (
                  <button
                    key={customer.id}
                    onClick={() => {
                      setSelectedCustomer(customer);
                      setShowCustomerDropdown(false);
                      setSearchTerm('');
                      setSelectedProvider(null);
                    }}
                    className="w-full px-4 py-3 text-left hover:bg-opacity-50 transition-colors border-b last:border-b-0"
                    style={{
                      backgroundColor: selectedCustomer?.id === customer.id ? 'var(--color-bg-secondary)' : 'var(--color-bg-primary)',
                      color: 'var(--color-text-primary)',
                      borderColor: 'var(--color-border)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                    }}
                    onMouseLeave={(e) => {
                      if (selectedCustomer?.id !== customer.id) {
                        e.currentTarget.style.backgroundColor = 'var(--color-bg-primary)';
                      }
                    }}
                  >
                    <div className="font-medium">{customer.name}</div>
                    {customer.description && (
                      <div className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                        {customer.description}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
          <p className="text-sm mt-2" style={{ color: 'var(--color-text-muted)' }}>
            Select your organization to access provider contact information
          </p>
        </div>
      </div>

      {/* Search with Autofill */}
      <div 
        className="rounded-xl shadow-sm border p-6"
        style={{
          backgroundColor: 'var(--color-bg-primary)',
          borderColor: 'var(--color-border)',
          boxShadow: 'var(--shadow-sm)',
          opacity: selectedCustomer ? 1 : 0.6,
          pointerEvents: selectedCustomer ? 'auto' : 'none'
        }}
      >
        <div className="relative">
          <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
            <Search className="w-4 h-4 inline mr-2" />
            Search for a provider
          </label>
          <div className="relative">
            <input
              type="text"
              placeholder={selectedCustomer ? "Type to search providers..." : "Select a customer first..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setTimeout(() => setIsSearchFocused(false), 150)}
              disabled={!selectedCustomer}
              className="w-full pl-4 pr-4 py-3 border rounded-lg transition-colors"
              style={{
                backgroundColor: 'var(--color-bg-secondary)',
                borderColor: 'var(--color-border)',
                color: 'var(--color-text-primary)',
                '--tw-ring-color': 'var(--color-accent)',
                cursor: selectedCustomer ? 'text' : 'not-allowed'
              } as React.CSSProperties}
            />
            
            {/* Autofill Suggestions */}
            {isSearchFocused && searchSuggestions.length > 0 && (
              <div 
                className="absolute top-full left-0 right-0 mt-1 border rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto dark-scrollbar"
                style={{
                  backgroundColor: 'var(--color-bg-primary)',
                  borderColor: 'var(--color-border)',
                  boxShadow: 'var(--shadow-lg)'
                }}
              >
                {searchSuggestions.map((suggestion) => (
                  <button
                    key={suggestion.id}
                    onClick={() => {
                      setSearchTerm(`${suggestion.title || 'Dr.'} ${suggestion.firstName} ${suggestion.lastName}`);
                      setSelectedProvider(suggestion);
                      // Hide menu after selection
                      setTimeout(() => setIsSearchFocused(false), 100);
                    }}
                    className="w-full px-4 py-3 text-left hover:bg-opacity-50 transition-colors border-b last:border-b-0"
                    style={{
                      backgroundColor: 'var(--color-bg-primary)',
                      color: 'var(--color-text-primary)',
                      borderColor: 'var(--color-border)',
                      '--tw-hover-bg-opacity': '0.5'
                    } as React.CSSProperties}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--color-bg-primary)';
                    }}
                  >
                    <div className="font-medium">{suggestion.title || 'Dr.'} {suggestion.firstName} {suggestion.lastName}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Single Provider Display */}
      {selectedProviderData ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-xl shadow-sm border overflow-hidden hover:shadow-lg transition-all duration-300"
          style={{
            backgroundColor: 'var(--color-bg-primary)',
            borderColor: 'var(--color-border)',
            boxShadow: 'var(--shadow-sm)'
          }}
        >
          {/* Provider Header */}
          <div 
            className="p-6 border-b"
            style={{ borderColor: 'var(--color-border)' }}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h2 className="text-2xl font-bold mb-2" style={{ color: 'var(--color-text-primary)' }}>
                  {selectedProviderData.firstName} {selectedProviderData.lastName}{selectedProviderData.title ? `, ${selectedProviderData.title}` : ''}
                </h2>
                
                {/* Specializations/Conditions - Blue text, comma-separated */}
                {selectedProviderData.conditions && selectedProviderData.conditions.length > 0 && (
                  <div className="mb-4" style={{ color: 'var(--color-accent, #3b82f6)' }}>
                    {selectedProviderData.conditions.map((cond, index) => (
                      <span key={cond.id}>
                        {cond.condition.name}
                        {index < selectedProviderData.conditions.length - 1 && ', '}
                  </span>
                    ))}
                  </div>
                )}
                </div>

              {/* Status Tags on the right */}
              <div className="flex flex-col gap-2 items-end">
                  {selectedProviderData.acceptsNewPatients === true && (
                    <span 
                    className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-full"
                      style={{ 
                      backgroundColor: '#10b981',
                        color: 'white'
                      }}
                    >
                    <Clock className="w-3 h-3 mr-1" />
                    New Patients
                    </span>
                  )}
                  
                {selectedProviderData.requiresReferral === true && (
                    <span 
                    className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-full"
                      style={{ 
                      backgroundColor: '#f59e0b',
                        color: 'white'
                      }}
                    >
                    Referral Required
                    </span>
                  )}
              </div>
            </div>
          </div>

          {/* Provider Content */}
          <div className="p-6 space-y-6">

            {/* APP's Section */}
            {selectedProviderData.apps && Array.isArray(selectedProviderData.apps) && selectedProviderData.apps.length > 0 && (
              <div className="mb-6 pb-6 border-b" style={{ borderColor: 'var(--color-border)' }}>
                <div className="flex items-center gap-2 mb-4">
                  <div className="p-2 rounded-lg" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                    <Stethoscope className="w-5 h-5" style={{ color: 'var(--color-accent)' }} />
                  </div>
                  <h4 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                    APP's
                  </h4>
                </div>
                <div className="flex flex-wrap gap-2">
                  {selectedProviderData.apps
                    .filter((app): app is { id: string; firstName: string; lastName: string; title?: string } => 
                      app !== null && app !== undefined && typeof app === 'object' && 'id' in app
                    )
                    .map((app) => (
                    <span 
                        key={app.id}
                        className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium shadow-sm"
                      style={{ 
                          backgroundColor: '#f97316',
                        color: 'white'
                      }}
                    >
                        {app.firstName} {app.lastName}{app.title ? ` - ${app.title}` : ''}
                    </span>
                    ))}
                </div>
              </div>
            )}

            {/* Doctor Section - same style as Provider Selection */}
            {selectedProviderData.supervisingProviders && selectedProviderData.supervisingProviders.length > 0 && (
              <div className="mb-6 pb-6 border-b" style={{ borderColor: 'var(--color-border)' }}>
                <div className="flex items-center gap-2 mb-4">
                  <div className="p-2 rounded-lg" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                    <Users className="w-5 h-5" style={{ color: 'var(--color-accent)' }} />
                  </div>
                  <h4 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                    Doctor
                  </h4>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {selectedProviderData.supervisingProviders.map((doc) => (
                    <span
                      key={doc.id}
                      className="inline-flex items-center px-2.5 py-1 rounded text-xs font-medium shadow-sm"
                      style={{
                        backgroundColor: '#3b82f6',
                        color: 'white'
                      }}
                    >
                      {doc.firstName} {doc.lastName}{doc.title ? ` - ${doc.title}` : ''}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Additional Information */}
            {(selectedProviderData.schedulingInstructions || selectedProviderData.insuranceNotes) && (
              <div className="mb-6 pb-6 border-b" style={{ borderColor: 'var(--color-border)' }}>
                <h4 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>
                  Additional Information
                </h4>
                <div 
                  className="p-5 rounded-xl border space-y-3"
                  style={{
                    backgroundColor: 'var(--color-bg-secondary)',
                    borderColor: 'var(--color-border)'
                  }}
                >
                  {selectedProviderData.schedulingInstructions && (
                    <div className="text-sm">
                      <div className="font-semibold mb-1" style={{ color: 'var(--color-text-primary)' }}>Scheduling:</div>
                      <RichTextDisplay
                        content={selectedProviderData.schedulingInstructions}
                        style={{ color: 'var(--color-text-secondary)' }}
                      />
                </div>
                  )}
                  {selectedProviderData.insuranceNotes && (
                    <div className="text-sm">
                      <div className="font-semibold mb-1" style={{ color: 'var(--color-text-primary)' }}>Insurance:</div>
                      <RichTextDisplay
                        content={selectedProviderData.insuranceNotes}
                        style={{ color: 'var(--color-text-secondary)' }}
                      />
              </div>
                  )}
            </div>
          </div>
            )}

            {/* Locations */}
            {selectedProviderData.locations && selectedProviderData.locations.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center" style={{ color: 'var(--color-text-primary)' }}>
                  <MapPin className="w-5 h-5 mr-2" />
                  Practice Locations
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedProviderData.locations.map((loc) => (
                    <div 
                      key={loc.id} 
                      className="p-4 rounded-lg border"
                      style={{
                        backgroundColor: 'var(--color-bg-secondary)',
                        borderColor: 'var(--color-border)'
                      }}
                    >
                      <h4 className="font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>
                        {loc.location.name}
                      </h4>
                      {loc.location.physicalAddress && (
                        <p className="text-sm mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                          {loc.location.physicalAddress}
                        </p>
                      )}
                      {loc.location.state && (
                        <p className="text-sm mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                          {loc.location.state.name} ({loc.location.state.code})
                        </p>
                      )}
                      {loc.address && <p className="text-sm mb-1" style={{ color: 'var(--color-text-secondary)' }}>{loc.address}</p>}
                      {loc.suite && <p className="text-sm mb-1" style={{ color: 'var(--color-text-secondary)' }}>Suite {loc.suite}</p>}
                      {loc.phone && (
                        <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                          Phone: {formatPhone(loc.phone)}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Contact Numbers */}
            {selectedProviderData.phoneNumbers && selectedProviderData.phoneNumbers.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center" style={{ color: 'var(--color-text-primary)' }}>
                  <Phone className="w-5 h-5 mr-2" />
                  Contact Numbers
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedProviderData.phoneNumbers.map((phoneNumber) => (
                    <div 
                      key={phoneNumber.id} 
                      className="p-3 rounded-lg border"
                      style={{
                        backgroundColor: 'var(--color-bg-secondary)',
                        borderColor: 'var(--color-border)'
                      }}
                    >
                      <div className="font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>
                        {phoneNumber.name}
                      </div>
                      <div className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                        {formatPhone(phoneNumber.phone)}
                      </div>
                      {phoneNumber.location && (
                        <div className="text-xs mt-1 space-y-1" style={{ color: 'var(--color-text-muted)' }}>
                          <div>{phoneNumber.location.name}</div>
                          {phoneNumber.location.physicalAddress && (
                            <div>{phoneNumber.location.physicalAddress}</div>
                          )}
                          {phoneNumber.location.state && (
                            <div>{phoneNumber.location.state.code}</div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Support Staff Contact Numbers */}
            {(selectedProviderData.maLvnPhone || selectedProviderData.triageNursePhone || selectedProviderData.surgerySchedulerPhone || selectedProviderData.practiceAdminPhone) && (
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center" style={{ color: 'var(--color-text-primary)' }}>
                  <User className="w-5 h-5 mr-2" />
                  Support Staff
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedProviderData.maLvnPhone && (
                    <div className="p-3 rounded-lg border" style={{ backgroundColor: 'var(--color-bg-secondary)', borderColor: 'var(--color-border)' }}>
                      <div className="font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>
                        MA/LVN: {selectedProviderData.maLvnName || 'Contact'}
                      </div>
                      <div className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                        {formatPhone(selectedProviderData.maLvnPhone)}
                      </div>
                    </div>
                  )}

                  {selectedProviderData.triageNursePhone && (
                    <div className="p-3 rounded-lg border" style={{ backgroundColor: 'var(--color-bg-secondary)', borderColor: 'var(--color-border)' }}>
                      <div className="font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>
                        Triage Nurse: {selectedProviderData.triageNurseName || 'Contact'}
                      </div>
                      <div className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                        {formatPhone(selectedProviderData.triageNursePhone)}
                      </div>
                    </div>
                  )}

                  {selectedProviderData.surgerySchedulerPhone && (
                    <div className="p-3 rounded-lg border" style={{ backgroundColor: 'var(--color-bg-secondary)', borderColor: 'var(--color-border)' }}>
                      <div className="font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>
                        Surgery Scheduler: {selectedProviderData.surgerySchedulerName || 'Contact'}
                      </div>
                      <div className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                        {formatPhone(selectedProviderData.surgerySchedulerPhone)}
                      </div>
                    </div>
                  )}

                  {selectedProviderData.practiceAdminPhone && (
                    <div className="p-3 rounded-lg border" style={{ backgroundColor: 'var(--color-bg-secondary)', borderColor: 'var(--color-border)' }}>
                      <div className="font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>
                        Practice Admin: {selectedProviderData.practiceAdminName || 'Contact'}
                      </div>
                      <div className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                        {formatPhone(selectedProviderData.practiceAdminPhone)}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Insurances */}
            {selectedProviderData.insurances && selectedProviderData.insurances.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center" style={{ color: 'var(--color-text-primary)' }}>
                  <Shield className="w-5 h-5 mr-2" />
                  Accepted Insurances
                </h3>
                <div 
                  className="max-h-48 overflow-y-auto p-3 rounded-lg border dark-scrollbar"
                  style={{
                    backgroundColor: 'var(--color-bg-secondary)',
                    borderColor: 'var(--color-border)'
                  }}
                >
                  <div className="flex flex-wrap gap-3">
                    {selectedProviderData.insurances.map((ins) => (
                      <span 
                        key={ins.id} 
                        className="px-4 py-2 text-base font-medium rounded-lg"
                        style={{
                          backgroundColor: 'rgba(59, 130, 246, 0.2)',
                          color: 'var(--color-text-primary)',
                          border: '1px solid rgba(59, 130, 246, 0.3)'
                        }}
                      >
                        {ins.insurance.name}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Conditions/Specialties */}
            {selectedProviderData.conditions && selectedProviderData.conditions.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center" style={{ color: 'var(--color-text-primary)' }}>
                  <Stethoscope className="w-5 h-5 mr-2" />
                  Specialties & Conditions
                </h3>
                <div 
                  className="max-h-48 overflow-y-auto p-3 rounded-lg border dark-scrollbar"
                  style={{
                    backgroundColor: 'var(--color-bg-secondary)',
                    borderColor: 'var(--color-border)'
                  }}
                >
                  <div className="flex flex-wrap gap-3">
                    {selectedProviderData.conditions.map((cond) => (
                      <span 
                        key={cond.id} 
                        className="px-4 py-2 text-base font-medium rounded-lg"
                        style={{
                          backgroundColor: 'rgba(34, 197, 94, 0.2)',
                          color: 'var(--color-text-primary)',
                          border: '1px solid rgba(34, 197, 94, 0.3)'
                        }}
                      >
                        {cond.condition.name}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Notes */}
            <div>
              <h3 className="text-lg font-semibold mb-3 flex items-center" style={{ color: 'var(--color-text-primary)' }}>
                <FileText className="w-5 h-5 mr-2" />
                Provider Notes
              </h3>

              {/* Existing Notes - Read Only */}
              {selectedProviderData.notes && selectedProviderData.notes.length > 0 ? (
                <div className="space-y-2">
                  {[...selectedProviderData.notes]
                    .sort((a: any, b: any) => a.order - b.order)
                    .map((note: any) => (
                      <div 
                        key={note.id} 
                        className="p-3 rounded-lg border"
                        style={{
                          backgroundColor: 'var(--color-bg-tertiary)',
                          borderColor: 'var(--color-border)'
                        }}
                      >
                        <CollapsibleRichText
                          content={note.content}
                          maxLines={5}
                          style={{ color: 'var(--color-text-primary)' }}
                        />
                        {note.category && (
                          <span 
                            className="inline-block mt-2 px-2 py-1 text-xs rounded"
                            style={{
                              backgroundColor: 'var(--color-bg-primary)',
                              color: 'var(--color-text-muted)'
                            }}
                          >
                            {note.category}
                          </span>
                        )}
                      </div>
                    ))}
                </div>
              ) : (
                <div 
                  className="text-center py-8 rounded-lg border-2 border-dashed"
                  style={{ 
                    borderColor: 'var(--color-border)',
                    backgroundColor: 'var(--color-bg-secondary)'
                  }}
                >
                  <FileText className="w-12 h-12 mx-auto mb-2" style={{ color: 'var(--color-text-muted)' }} />
                  <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                    No notes available for this provider.
                  </p>
                </div>
              )}
            </div>

            {/* Metadata */}
            {/* <div className="pt-4 border-t" style={{ borderColor: 'var(--color-border)' }}>
              <div className="flex items-center justify-between text-xs" style={{ color: 'var(--color-text-muted)' }}>
                <span>{formatDate(selectedProviderData.createdAt)}</span>
                {selectedProviderData.updatedAt !== selectedProviderData.createdAt && (
                  <span>{formatDate(selectedProviderData.updatedAt)}</span>
                )}
              </div>
            </div> */}
          </div>
        </motion.div>
      ) : null}

      {/* Empty State (standardized) */}
      {!selectedProviderData && searchTerm && (
        <div className="text-center py-8">
          <User className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--color-text-muted)' }} />
          <p style={{ color: 'var(--color-text-secondary)' }}>No provider found.</p>
          <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>Try a different name or check the spelling.</p>
        </div>
      )}

      {/* Initial State (standardized) */}
      {!selectedProviderData && !searchTerm && (
        <div className="text-center py-8">
          <User className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--color-text-muted)' }} />
          {!selectedCustomer ? (
            <>
              <p style={{ color: 'var(--color-text-secondary)' }}>Select a customer organization to view provider contacts</p>
              <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>Choose your organization from the dropdown above</p>
            </>
          ) : (
            <>
              <p style={{ color: 'var(--color-text-secondary)' }}>Search for a provider using the field above.</p>
              <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>Start typing to see matching providers.</p>
            </>
          )}
        </div>
      )}
      </div>
    </>
  );
};
